/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

export const PROMPT_TYPES = [
	{
		value: "TTS",
		label: "Text",
	},
	{ value: "SSML", label: "SSML" },
	{ value: "audio", label: "Prompt Name" },
];

export const ROLE_TYPES = ["Admin", "Agent"];

export const SET_VALUE_CALLBACK_DEBOUNCE_RATE = 100;

export const LEX_BOT_BUILD_STATUS_REFRESH_RATE = 15000;
export const LEX_BOT_LOCALES = {
	"en-US": {
		language: "English",
		voiceIdList: ["Joey", "Kimberly", "Kendra", "Justin", "Ivy", "Matthew", "Kevin", "Joanna", "Salli"],
	},
	"es-US": { language: "Spanish", voiceIdList: ["Lupe", "Penelope", "Miguel"] },
};
export const LANGUAGE_MAXLENGTH = 2;
export const NAMESPACE_MAXLENGTH = 20;
export const PM_SETTINGS_KEY = "prompts_manager_settings";

export const QUEUE_SETTING_OBJECT_NAME = "queue_settings_queueId_";

export const QUEUE_DEFAULT_TIMEZONE = "US/Eastern";

export const CONNECT_REPORT_ENDING_PATTERN = "-YYYY-MM-DDThh:mm:ssZ.csv";

export const TERMINOLOGY_FILE_NAME = "PACE-WebChat-Translation-Terminology-";

export const PACE_PINPOINT_SMS_ESCALATION_APPLICATION_ID = "PACE_PINPOINT_SMS_ESCALATION_APPLICATION";

export const DEFAULT_INTEGRATION_APPLICATIONS = {
	pinpoint: [
		{
			applicationId: PACE_PINPOINT_SMS_ESCALATION_APPLICATION_ID,
			defaultApplication: true,
			platform: "pinpoint",
			contactFlowId: "",
			friendlyName: "Voice to SMS Application",
			phoneNumber: "",
			projectId: "",
		},
	],
};

export const SUPPORTED_LANGUAGES = [
	{
		displayName: "English",
		languageCode: "en",
	},
	{
		displayName: "Bengali",
		languageCode: "bn",
	},
	{
		displayName: "Chinese (Simplified)",
		languageCode: "zh",
	},
	{
		displayName: "Chinese (Traditional)",
		languageCode: "zh-TW",
	},
	{
		displayName: "French",
		languageCode: "fr",
	},
	{
		displayName: "French (Canada)",
		languageCode: "fr-CA",
	},
	{
		displayName: "Hindi",
		languageCode: "hi",
	},
	{
		displayName: "Korean",
		languageCode: "ko",
	},
	{
		displayName: "Portuguese",
		languageCode: "pt",
	},
	{
		displayName: "Russian",
		languageCode: "ru",
	},
	{
		displayName: "Spanish",
		languageCode: "es",
	},
	{
		displayName: "Tagalog",
		languageCode: "tl",
	},
];

export const ACVM_WEB_URL_KEY = "ACVM_WEB_URL";

export const ADMINBACKEND_PROXY_DASHBOARD_URL = "ADMINBACKEND_PROXY_DASHBOARD_URL"; // Api gateway http proxy uri for opensearch dashboards.
export const ADMIN_OPENSEARCH_DASHBOARD_URL = "ADMIN_OPENSEARCH_DASHBOARD_URL"; // Dashboard url created from Opensearch app.
export const ADMIN_DEFAULT_DASHBOARD_URL = "/_dashboards/app/dashboards"; // default opensearch dashboards url incase not configured any dashboards.

export const MANAGER_ROLE = "Manager";
export const ADMIN_ROLE = "Admin";
export const AGENT_ROLE = "Agent";

export const SURVEY_QUESTION_TYPE = {
	SCALE: "scale",
	BINARY: "binary",
	TEXT: "text",
};

export const AGENT_CONSOLE_LAYOUT_CONFIG_KEY = "AgentConsoleLayoutSettings";
export const TEAMS_SYNC_CONFIGURATION_CONFIG_KEY = "TeamsSyncConfiguration";
export const METRICS_TIMEZONE_CONFIGURATION_CONFIG_KEY = "MetricsTimezoneConfiguration";

export const TAB_VARIANTS = {
	static: "static",
	dynamic: "dynamic",
};

export const TAB_TYPES = {
	attribute: "attribute",
	builtIn: "built-in",
	embed: "embed",
	amazon: "amazon",
};

export const TAB_TYPE_LABELS = {
	attribute: "Attribute",
	builtIn: "Built-in",
	embed: "Embed",
};

export const AMAZON_FEATURE_TYPE = {
	customerProfiles: "customerProfiles",
	wisdom: "wisdom",
};

export const BUILT_IN_TABS = {
	general: {
		id: "general",
		displayName: "General",
		description: "Displays general attributes",
		sortOrder: 1,
		type: TAB_TYPES.attribute,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
		config: {
			attributePrefix: "general",
		},
	},
	contact_history: {
		id: "contact_history",
		displayName: "Contact Path",
		description: "Displays contact path",
		sortOrder: 3,
		type: TAB_TYPES.builtIn,
		variant: TAB_VARIANTS.dynamic,
		hidden: false,
	},
	notes: {
		id: "notes",
		displayName: "Notes",
		description: "Displays notes, dispositions & sub-dispositions",
		sortOrder: 4,
		type: TAB_TYPES.builtIn,
		variant: TAB_VARIANTS.dynamic,
		hidden: false,
	},
	canned_responses: {
		id: "canned_responses",
		displayName: "Canned Responses",
		description: "Displays canned responses",
		sortOrder: 5,
		type: TAB_TYPES.builtIn,
		variant: TAB_VARIANTS.dynamic,
		hidden: false,
	},
	external_transfers: {
		id: "external_transfers",
		displayName: "External Contacts",
		description: "Transfer a call to an external contact.",
		sortOrder: 6,
		type: TAB_TYPES.builtIn,
		variant: TAB_VARIANTS.dynamic,
		hidden: false,
	},
	connect_customer_profiles: {
		id: "connect_customer_profiles",
		displayName: "Customer Profiles",
		description: "Displays Amazon Connect Customer Profiles",
		sortOrder: 7,
		type: TAB_TYPES.amazon,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
		config: {
			amazonConnectFeature: AMAZON_FEATURE_TYPE.customerProfiles,
		},
	},
	connect_wisdom: {
		id: "connect_wisdom",
		displayName: "Wisdom",
		description: "Displays Amazon Connect Wisdom",
		sortOrder: 8,
		type: TAB_TYPES.amazon,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
		config: {
			amazonConnectFeature: AMAZON_FEATURE_TYPE.wisdom,
		},
	},
	connect_realtime_sentiment_analysis: {
		id: "connect_realtime_sentiment_analysis",
		displayName: "Sentiment Analysis",
		description: "Displays Amazon Connect Realtime Sentiment Analysis",
		sortOrder: 9,
		type: TAB_TYPES.builtIn,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
	},
	pinpoint_outbound_sms: {
		id: "pinpoint_outbound_sms",
		displayName: "Send SMS",
		description: "Sends an outbound SMS message from a selected phone number",
		sortOrder: 10,
		type: TAB_TYPES.builtIn,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
	},
	voicemail_task_tab: {
		id: "voicemail_task_tab",
		displayName: "Voicemail",
		description: "Displays Voicemail contact details and voicemail information.",
		sortOrder: 11,
		type: TAB_TYPES.builtIn,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
		config: {
			canStatic: false,
			limitQueueVisibility: false,
		},
	},
	email: {
		id: "email",
		displayName: "Email",
		description: "View email communications",
		sortOrder: 12,
		type: TAB_TYPES.builtIn,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
		config: {
			canStatic: false,
			limitQueueVisibility: false,
		},
	},
	video_meeting_tab: {
		id: "video_meeting_tab",
		displayName: "Meeting",
		description: "Allows to make screen sharing, audio, video meetings with the customer.",
		sortOrder: 13,
		type: TAB_TYPES.builtIn,
		hidden: false,
		variant: TAB_VARIANTS.dynamic,
		config: {
			canStatic: false,
			limitQueueVisibility: false,
		},
	},
};

export const THIRD_PARTY_APPLICATION_TYPES = {
	PINPOINT: "pinpoint",
	AZURE: "azure",
	FACEBOOK: "facebook",
	EMAIL: "email",
};

// default tab settings that should always be present in the config
export const DEFAULT_SETTING = {
	tabs: ["general", "contact_history", "canned_responses", "notes", "email"],
};

export const LAYOUT_CONFIG_KEY = "AgentConsoleLayoutSettings";

// The colors of the sentiments in the sentiment analysis tab.
export const SENTIMENT_TYPE = {
	POSITIVE: { color: "green", weightingValue: 1 },
	NEUTRAL: { color: "", weightingValue: 0 },
	NEGATIVE: { color: "red", weightingValue: -1 },
};

export const SENTIMENT_AVERAGE_VALUES = {
	POSITIVE: {
		value: 0.33,
		color: "green",
		icon: "far fa-smile",
		title: "POSITIVE",
	},
	NEGATIVE: {
		value: -0.33,
		color: "red",
		icon: "far fa-frown",
		title: "NEGATIVE",
	},
	NEUTRAL: {
		value: 0,
		color: "black",
		icon: "far fa-meh",
		title: "NEUTRAL",
	},
};

export const ATTRIBUTE_EXCLUSION_LIST = {
	extensionNumber: true,
	agentId: true,
	agentName: true,
	transferMessage: true,
	transcribeVoicemail: true,
	saveCallRecording: true,
	encryptVoicemail: true,
	available: true,
	startStreamingAudioStatus: true,
};

export const CCP_ENUMS = {
	statusCSSColors: {},
	pages: {
		DIAL_PAD: "dialPad",
		QUICK_CONNECTS: "quickConnects",
		SETTINGS: "settings",
		ACTIVE_CALL: "activeCall",
		AGENT_STATE_SELECT: "agentStateSelect",
		HOME: "home",
	},
	agentPhoneTypes: {
		DESK: "desk",
		SOFT: "soft",
	},
	stateNames: {
		ACW: "After call work",
		AGENT_HOLD: "Agent on hold",
		CONNECTED: "Connected",
		CONNECTING: "Connecting",
		DISCONNECTED: "<disconnected>",
		ERROR: "Error",
		INBOUND: "Inbound call",
		INCOMING: "Incoming",
		INCOMING_CALLBACK: "Callback incoming",
		INITIALIZATION_FAILURE: "Initialization failed",
		INITIALIZING: "Initializing...",
		JOINED: "Joined",
		MISSED_CALL: "Missed call",
		MONITORING: "Monitoring",
		MUTED: "MUTED",
		ON_HOLD: "On hold",
		OUTBOUND: "Outbound call",
	},
	callDirections: {
		INBOUND: "inbound",
		OUTBOUND: "outbound",
	},
	acceptedSymbols: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
	localStorageKeys: {
		// Local storage keys
		OUTBOUND_ISO_CODE: "outboundISOCountryCode",
		OUTBOUND_DIAL_CODE: "outboundCountryDialCode",
		HARDPHONE_ISO_CODE: "hardphoneISOCountryCode",
		HARDPHONE_DIAL_CODE: "hardphoneCountryDialCode",
		SHOW_HARDPHONE_CONFIG: "showHardphoneConfig",
		HARDPHONE_RADIO_BUTTON_CHECKED: "hardphoneRadioButtonChecked",
		SOFTPHONE_RADIO_BUTTON_CHECKED: "softphoneRadioButtonChecked",
	},
	loggerLevels: {
		WARNING: "[WARNING] :",
		INFO: "[INFO] :",
		ERROR: "[ERROR] :",
		FATAL: "[FATAL] :",
	},
};

export const CCP_STATES = {
	// Taken from window.connect object in default ccp.
	// Last updated 12-20-18
	contactStateTypes: {
		CONNECTED: "connected",
		CONNECTING: "connecting",
		ENDED: "ended",
		ERROR: "error",
		INCOMING: "incoming",
		INIT: "init",
		MISSED: "missed",
		PENDING: "pending",
	},
	connectionStateTypes: {
		CONNECTED: "connected",
		CONNECTING: "connecting",
		DISCONNECTED: "disconnected",
		HOLD: "hold",
		INIT: "init",
	},
	// "system" and "error" is not in the connect object, but appears in practice.
	agentStateTypes: {
		INIT: "init",
		ROUTABLE: "routable",
		NOT_ROUTABLE: "not_routable",
		OFFLINE: "offline",
		SYSTEM: "system",
		ERROR: "error",
	},
	agentErrorTypes: {
		AGENT_HUNG_UP: "AgentHungUp",
		BAD_ADDRESS_AGENT: "BadAddressAgent",
		BAD_ADDRESS_CUSTOMER: "BadAddressCustomer",
		DEFAULT: "Default",
		ERROR: "Error",
		FAILED_CONNECT_AGENT: "FailedConnectAgent",
		FAILED_CONNECT_CUSTOMER: "FailedConnectCustomer",
		LINE_ENGAGED_AGENT: "LineEngagedAgent",
		LINE_ENGAGED_CUSTOMER: "LineEngagedCustomer",
		MISSED_CALL_AGENT: "MissedCallAgent",
		MISSED_CALL_CUSTOMER: "MissedCallCustomer",
		MULTIPLE_CCP_WINDOWS: "MultipleCcpWindows",
		REALTIME_COMMUNICATION_ERROR: "RealtimeCommunicationError",
	},
	stateObjects: {
		AGENT: "agent",
		CONTACT: "contact",
		PRIMARYCONNECTION: "primary_connection",
		SECONDARYCONNECTION: "secondary_connection",
	},
};

export const SLOT_TYPE_TYPE = {
	CUSTOM: "CUSTOM",
	BUILT_IN: "BUILT_IN",
};

export const AGENT_STATUS_ALERTS = {
	ALERT_TIME_UNITS: 60 * 1000,
	ALERT_MESSAGE_LENGTH: 200,
	ALERT_MAX_TIME: 24 * 60,
};
export const NOTES_CHARACTER_LIMIT = 1000;

export const SMS_CHARACTER_LIMIT = 1000;

/**
 * https://docs.aws.amazon.com/connect/latest/adminguide/play.html#:~:text=to%20prompts.-,Configuration%20tips,-For%20step%2Dby
 */
export const PROMPT_MESSAGE_MAX_LENGTH = 6000;

export const AVAILABLE_SMS_COUNTRIES = (process.env.REACT_APP_AVAILABLE_SMS_COUNTRIES || "").split(",");

export const PACE_DASHBOARDS = {
	REPORT_NAME_MAX_LEN: 50,
};

export const URL_CONFIG_KEYS = {
	ADMIN_API_URL: "ADMIN_API_URL",
	SHARED_API_URL: "SHARED_API_URL",
	ACVM_BACKEND_URL: "ACVM_BACKEND_URL",
	CALLMENOW_MODULE_JS_URL: "CALLMENOW_MODULE_JS_URL",
	WEBCHAT_MODULE_JS_URL: "WEBCHAT_MODULE_JS_URL",
	SHARED_WEBSOCKET_API_URL: "SHARED_WEBSOCKET_API_URL",
};

export const CONTACT_TYPES = {
	VOICE: "VOICE",
	CHAT: "CHAT",
	TASK: "TASK",
};

export const CUSTOMER_PLATFORMS = {
	WEB_CHAT: "Web Chat",
	VOICEMAIL: "Voicemail",
	EMAIL: "Email",
	SMS: "SMS",
	TEAMS: "Microsoft Teams",
	FACEBOOK: "Facebook",
	INSTAGRAM: "Instagram",
};

export const MEETING_ACTIONS = {
	AGENT_INIT: "[SYSTEM] AGENT INITIATED MEETING",
	AGENT_ENDED: "[SYSTEM] AGENT ENDED THE MEETING",
	AGENT_ACCEPTED: "[SYSTEM] AGENT ACCEPTED MEETING",
	AGENT_REJECTED: "[SYSTEM] AGENT REJECTED MEETING",
	CUST_REQUESTED: "[SYSTEM] CUSTOMER REQUESTED MEETING",
	CUST_ENDED: "[SYSTEM] CUSTOMER ENDED MEETING",
	CUST_ACCEPTED: "[SYSTEM] CUSTOMER JOINED MEETING",
	CUST_REJECTED: "[SYSTEM] CUSTOMER REJECTED MEETING",
	CUST_MEETING_INTERRUPTED: "[SYSTEM] MEETING INTERRUPTED AT CUSTOMER SIDE",
};

export const MEETING_BUTTON_HELPER_MSGS = {
	NO_ACTIVE_CONTACTS: "No active contacts",
	ACCEPT_MEETING: "The customer has requested a meeting to enable video and screen sharing",
	OTHER_MEETING_INPROGRESS: "Unable to start a meeting, another meeting is currently in progress",
	CUST_MEDIA_NOT_SUPPORTED: "The customer does not support video and screen sharing",
};

export const TEAMS_USER_ACTIVITIES = {
	Away: "Away",
	BeRightBack: "Be Right Back",
	Busy: "Busy",
	DoNotDisturb: "Do Not Disturb",
	InACall: "In A Call",
	InAConferenceCall: "In A Conference Call",
	Inactive: "Inactive",
	InAMeeting: "In A Meeting",
	Offline: "Offline",
	OffWork: "Off Work / Appear offline",
	OutOfOffice: "Out Of Office",
	Presenting: "Presenting",
	UrgentInterruptionsOnly: "Urgent Interruptions Only",
	PresenceUnknown: "Presence Unknown",
};

export const AGENT_CONNECT_STATES = {
	VoiceIncoming: "Voice: Incoming",
	VoiceOnContact: "Voice: On Contact",
	VoiceACW: "Voice: After Call Work",
	ChatTaskMaxCapacity: "Chat/Task: Maximum Capacity",
};

export const TEAMS_STATUS_UPDATE_ACTION_TYPES = {
	SET_PRESENCE: "SET_PRESENCE",
	CLEAR_PRESENCE: "CLEAR_PRESENCE",
};
/**
 * websocket event types constants
 */
export const eventTypes = {
	TEAMS_STATUS_UPDATE: "TEAMS_STATUS_UPDATE",
};

export const REDACTED_STRING = "*****";

export const PII_FIELDS = {
	externalContact: [
		{ path: "$..fullName", keepFirst: 3 },
		{ path: "$..phone", keepLast: 3 },
	],
	contact: ["$..attributes", { path: "$..phone", keepLast: 3 }],
	agent: [{ path: "$..username", keepFirst: 3 }],
};

export const MAX_EMAIL_SIZE_MB = 30;
